import React, {useCallback, useContext, useState} from 'react';

import {FormInput} from 'src/components/input';
import {CustomValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {useFormatInputValue} from 'src/hooks/useFormatInputValue';
import {getAccountsByName} from 'src/openapi/admin/apis/GetAccountsByNameApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    ACCOUNT_MAME_CHECK_ERROR,
    ACCOUNT_NAME_IS_ALREADY_USED,
    VALIDATION_ERRORS,
} from '../constants';

import type {ValidationContextType} from 'src/contexts/validation-context';

type Props = {
    id?: string,
    label: React.ReactNode,
    accountId?: number,
    disabled?: boolean,
    accountName: string,
    onChange: (accountName: string) => void,
};

export const AccountNameInput = (props: Props) => {
    const {accountId, onChange, disabled, label} = props;
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext<ValidationContextType>(ValidationContext);

    const [busy, setBusy] = useState(false);

    useFormatInputValue({
        value: props.accountName,
        setValue: onChange,
        regex: /[^0-9 A-Za-z$&+,:{};_=?@#|'\]<>.^*()%!-\\/]/g,
    });

    const onBlur = useCallback((accountName: string) => {
        if (accountName) {
            setBusy(true);
            apiDispatch(getAccountsByName({accountName})).then(response => {
                if (response.payload.success) {
                    if (response.payload.data.some(account =>
                        accountName.toLowerCase() === account.accountName.toLowerCase() &&
                        account.accountId !== accountId,
                    )) {
                        addError({
                            code: ACCOUNT_NAME_IS_ALREADY_USED,
                            message: VALIDATION_ERRORS[ACCOUNT_NAME_IS_ALREADY_USED],
                            controlId: 'account-name',
                        });
                    } else {
                        removeError('account-name');
                    }
                } else {
                    addError({
                        code: ACCOUNT_MAME_CHECK_ERROR,
                        message: VALIDATION_ERRORS[ACCOUNT_MAME_CHECK_ERROR],
                        controlId: 'account-name',
                    });
                }
                setBusy(false);
            });
        } else {
            removeError('account-name');
        }

        onChange(accountName);
    }, [accountId, addError, apiDispatch, onChange, removeError]);

    const getValid = useCallback((error: string | undefined) => {
        if (error) {
            return false;
        } else if (props.accountName) {
            return true;
        } else {
            return undefined;
        }
    }, [props.accountName]);

    return (
        <CustomValidatedControl name="account-name">
            {error => (
                <FormInput
                    maxLength={255}
                    label={label}
                    readOnly={disabled}
                    placeholder="Account name"
                    value={props.accountName}
                    busy={busy}
                    valid={getValid(error)}
                    errorDescription={error}
                    onBlur={onBlur}
                />
            )}
        </CustomValidatedControl>
    );
};
